/**
 * GSAP benefit marquee — lazy-loads GSAP + ScrollTrigger on first interaction.
 */
(function () {
  const GSAP_SRC = "https://cdnjs.cloudflare.com/ajax/libs/gsap/3.13.0/gsap.min.js";
  const ST_SRC = "https://cdnjs.cloudflare.com/ajax/libs/gsap/3.13.0/ScrollTrigger.min.js";

  let loadPromise = null;
  let started = false;

  function loadScriptOnce(src) {
    return new Promise((resolve, reject) => {
      const existing = document.querySelector(`script[src="${src}"]`);
      if (existing) {
        if (existing.dataset.loaded === "true") return resolve();
        existing.addEventListener("load", () => resolve(), { once: true });
        existing.addEventListener("error", reject, { once: true });
        return;
      }

      const s = document.createElement("script");
      s.src = src;
      s.async = true;
      s.onload = () => {
        s.dataset.loaded = "true";
        resolve();
      };
      s.onerror = reject;
      document.head.appendChild(s);
    });
  }

  function ensureGSAP() {
    if (window.gsap && window.ScrollTrigger) return Promise.resolve();
    if (loadPromise) return loadPromise;

    loadPromise = (async () => {
      await loadScriptOnce(GSAP_SRC);
      await loadScriptOnce(ST_SRC);
      if (window.gsap && window.ScrollTrigger) {
        gsap.registerPlugin(ScrollTrigger);
      }
    })();

    return loadPromise;
  }

  function whenVisible(el, cb) {
    if (!("IntersectionObserver" in window)) return cb();
    const io = new IntersectionObserver(
      (entries) => {
        for (const e of entries) {
          if (e.isIntersecting) {
            io.disconnect();
            cb();
            break;
          }
        }
      },
      { threshold: 0.01 }
    );
    io.observe(el);
  }

  function start() {
    if (started) return;
    started = true;

    ensureGSAP()
      .then(() => {
        document.querySelectorAll("[data-marquee]").forEach((marqueeContainer) => {
          if (marqueeContainer.dataset.scriptInitialized) return;
          marqueeContainer.dataset.scriptInitialized = "true";

          const marqueeContent = marqueeContainer.querySelector("[data-marquee-content]");
          if (!marqueeContent) return;

          let loop;
          let progress = 0;
          const speed = (parseFloat(marqueeContainer.dataset.speed) || 1) * 100;

          function createMarqueeAnimation() {
            if (loop) {
              progress = loop.progress();
              loop.pause().revert();
            }

            marqueeContainer.querySelectorAll(".clone").forEach((clone) => clone.remove());

            const containerWidth = marqueeContainer.offsetWidth;
            let contentWidth = marqueeContent.offsetWidth;

            const createClone = () => {
              const clone = marqueeContent.cloneNode(true);
              clone.classList.add("clone");
              clone.removeAttribute("data-marquee-content");
              marqueeContainer.appendChild(clone);
              contentWidth += marqueeContent.offsetWidth;
            };

            do {
              createClone();
            } while (contentWidth < containerWidth * 2) {
              createClone();
            }

            const items = gsap.utils.toArray(marqueeContainer.children);
            loop = gsap.timeline({
              repeat: -1,
              paused: true,
              defaults: { ease: "none" },
              scrollTrigger: {
                trigger: marqueeContainer,
                start: () => "top bottom",
                end: () => "bottom top",
                toggleActions: "play pause resume pause",
              },
            });

            loop.to(items, {
              xPercent: -100,
              duration: (contentWidth / speed).toFixed(0),
              modifiers: {
                xPercent: gsap.utils.wrap(-100, 0),
              },
            });

            loop.progress(progress).play();

            marqueeContainer.addEventListener("mouseenter", () =>
              gsap.to(loop, { timeScale: 0, overwrite: true })
            );
            marqueeContainer.addEventListener("mouseleave", () =>
              gsap.to(loop, { timeScale: 1, overwrite: true })
            );
          }

          createMarqueeAnimation();

          let resizeTimeout;
          function handleResize() {
            clearTimeout(resizeTimeout);
            resizeTimeout = setTimeout(() => {
              createMarqueeAnimation();
              ScrollTrigger.refresh(true);
            }, 200);
          }

          window.addEventListener("resize", handleResize, { passive: true });
        });
      })
      .catch((err) => {
        console.error("GSAP lazy-load failed:", err);
      });
  }

  function setupTriggers() {
    ["mousemove", "touchstart", "scroll"].forEach((evt) => {
      window.addEventListener(evt, start, { once: true, passive: true });
    });

    const firstMarquee = document.querySelector("[data-marquee]");
    if (firstMarquee) whenVisible(firstMarquee, start);
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", setupTriggers);
  } else {
    setupTriggers();
  }
})();
